import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { GlobalApprovalMode, ReviewerApproverMode } from '@mg/shared';
import type { PlatformSettings } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useIsTenantAdmin } from '../../auth/useWorkspaceAdmin';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { FormField, Select } from '../../components/ui/Field';
import { Button } from '../../components/ui/Button';
import { CHANGES_SAVED, useToast } from '../../components/ui/toast';
import { useFooterSave } from '../../components/settings/footerSave';

const GLOBAL_MODE_OPTIONS: { value: GlobalApprovalMode; label: string }[] = [
  { value: GlobalApprovalMode.CAMPAIGN, label: 'Campaign level' },
  { value: GlobalApprovalMode.POST, label: 'Per-Post level' },
  { value: GlobalApprovalMode.NONE, label: 'No approvals' },
  { value: GlobalApprovalMode.PER_WORKSPACE, label: 'Set by each Brand Workspace' },
];

const REVIEW_FLOW_OPTIONS: { value: ReviewerApproverMode; label: string }[] = [
  { value: ReviewerApproverMode.BOTH, label: 'Reviewer then Approver' },
  { value: ReviewerApproverMode.APPROVER_ONLY, label: 'Approver only' },
  { value: ReviewerApproverMode.PER_WORKSPACE, label: 'Set by each Brand Workspace' },
];

/**
 * Install-wide (org-level) approval policy — a single {@link PlatformSettings} row shared by
 * every Brand Workspace. Visible to all members so they can see the policy; editable only by a
 * Tenant Admin (the API also enforces it). See docs/roles-approval-overhaul-design.md §3. Its own
 * tab on App Settings, where the page's sticky Save footer saves it for a Tenant Admin.
 */
export function PlatformSettingsCard() {
  const query = useQuery({
    queryKey: ['platform-settings'],
    queryFn: ({ signal }) => api.getPlatformSettings(signal),
  });

  return (
    <Card>
      <CardHeader
        title="Approval settings (all workspaces)"
        description="Install-wide policy for how content is reviewed before it can be scheduled or published."
      />
      <CardBody>
        {query.isLoading ? (
          <p className="text-sm text-ink-faint">Loading approval settings…</p>
        ) : query.isError ? (
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load approval settings.</p>
        ) : query.data ? (
          <PlatformSettingsForm settings={query.data} />
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * Seeded from the loaded `settings` prop and compared against it each render, so after a
 * successful save (which refetches the query and flows the new values back in) the form is no
 * longer dirty and the "Saved" indicator sticks — no remount/key needed.
 */
function PlatformSettingsForm({ settings }: { settings: PlatformSettings }) {
  const canEdit = useIsTenantAdmin();
  const queryClient = useQueryClient();

  const [globalMode, setGlobalMode] = useState<GlobalApprovalMode>(settings.globalApprovalMode);
  const [reviewFlow, setReviewFlow] = useState<ReviewerApproverMode>(settings.reviewerApproverMode);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    globalMode !== settings.globalApprovalMode || reviewFlow !== settings.reviewerApproverMode;

  const save = useMutation({
    mutationFn: () =>
      api.updatePlatformSettings({
        globalApprovalMode: globalMode,
        reviewerApproverMode: reviewFlow,
      }),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not save approval settings.'),
  });

  const toast = useToast();
  /** Saves the change; resolves to whether it worked (a failure shows the server's sentence here). */
  const saveChanges = async (): Promise<boolean> => {
    setError(null);
    try {
      await save.mutateAsync();
      return true;
    } catch {
      return false;
    }
  };
  // On App Settings the page's sticky Save footer saves this form, and its own Save stands down.
  const { inFooter } = useFooterSave({
    id: 'approval-settings',
    dirty,
    save: saveChanges,
    discard: () => {
      setError(null);
      setSaved(false);
      setGlobalMode(settings.globalApprovalMode);
      setReviewFlow(settings.reviewerApproverMode);
    },
  });

  const overrides = globalMode !== GlobalApprovalMode.PER_WORKSPACE;
  // With approvals off install-wide, the reviewer flow has nothing to apply to.
  const approvalsOff = globalMode === GlobalApprovalMode.NONE;
  const showSaved = saved && !dirty;

  return (
    <div className="space-y-5">
      <FormField
        label="Global approval mode"
        htmlFor="global-approval-mode"
        hint={
          overrides
            ? 'This overrides every Brand Workspace’s own approval setting.'
            : 'Each Brand Workspace chooses its own approval mode on its Edit Brand Workspace page.'
        }
      >
        <Select
          id="global-approval-mode"
          value={globalMode}
          disabled={!canEdit || save.isPending}
          onChange={(e) => {
            setSaved(false);
            setGlobalMode(e.target.value as GlobalApprovalMode);
          }}
        >
          {GLOBAL_MODE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField
        label="Review flow"
        htmlFor="review-flow"
        hint={
          approvalsOff
            ? 'Approvals are off, so the reviewer flow doesn’t apply.'
            : 'Applies to the two-stage review flow.'
        }
      >
        <Select
          id="review-flow"
          value={reviewFlow}
          disabled={!canEdit || save.isPending || approvalsOff}
          onChange={(e) => {
            setSaved(false);
            setReviewFlow(e.target.value as ReviewerApproverMode);
          }}
        >
          {REVIEW_FLOW_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FormField>

      {canEdit ? (
        // With the footer saving, the row keeps only the "Saved" and error lines, and is left out when
        // it has neither.
        (!inFooter || showSaved || error) && (
          <div className="flex items-center gap-3">
            {!inFooter && (
              <Button
                onClick={() =>
                  void saveChanges().then((ok) => {
                    if (ok) toast.show(CHANGES_SAVED);
                  })
                }
                loading={save.isPending}
                disabled={save.isPending || !dirty}
              >
                Save
              </Button>
            )}
            {showSaved && (
              <span className="flex items-center gap-1 text-sm text-success dark:text-success-dark">
                <Check className="h-4 w-4" />
                Saved
              </span>
            )}
            {error && <span className="text-sm text-red-600 dark:text-error-dark">{error}</span>}
          </div>
        )
      ) : (
        <p className="text-xs text-ink-faint">Only a Tenant Admin can change these.</p>
      )}
    </div>
  );
}
